import { Mic, Plus, SendHorizontal, Trash2 } from 'lucide-react';
import { type KeyboardEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@farmos/ui/button';
import { shrinkImage } from '@farmos/ui/image';
import { cn } from '@farmos/ui/utils';

export interface OutFile {
  body: Blob;
  /** without codec parameters, as storage expects */
  mime: string;
  ext: string;
  duration_ms?: number;
}

/** What the phone can record: opus in WebM (Chrome, Android), AAC in MP4 (iOS Safari). */
function recordingType() {
  for (const t of ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'])
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  return '';
}

const CANCEL_DISTANCE = 80;
const MIN_MS = 600;

/**
 * Text, photos, and a WhatsApp-style mic: hold to record, release to send, slide away to
 * cancel. From the keyboard, Enter or Space starts and stops a recording.
 */
export function Composer({
  disabled,
  onSend,
}: {
  disabled?: boolean;
  onSend: (text: string, files: OutFile[]) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [rec, setRec] = useState<{ start: number; x: number; y: number; cancel: boolean } | null>(
    null,
  );
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const photo = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!rec) return;
    const id = setInterval(() => setElapsed(Date.now() - rec.start), 200);
    return () => clearInterval(id);
  }, [rec]);
  useEffect(() => () => recorder.current?.stream.getTracks().forEach((tr) => tr.stop()), []);

  const send = () => {
    const v = text.trim();
    if (!v || disabled) return;
    onSend(v, []);
    setText('');
    box.current?.focus();
  };

  const start = async (x: number, y: number) => {
    if (disabled || recorder.current) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error(t('chat.mic_denied'));
      return;
    }
    const type = recordingType();
    const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks: Blob[] = [];
    const began = Date.now();
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      const keep = (r as MediaRecorder & { keep?: boolean }).keep;
      const ms = Date.now() - began;
      if (!keep || ms < MIN_MS) return;
      const mime = (r.mimeType || type || 'audio/webm').split(';')[0]!;
      onSend('', [
        {
          body: new Blob(chunks, { type: mime }),
          mime,
          ext: mime === 'audio/mp4' ? 'm4a' : mime.split('/')[1]!,
          duration_ms: ms,
        },
      ]);
    };
    r.start();
    recorder.current = r;
    setElapsed(0);
    setRec({ start: began, x, y, cancel: false });
  };

  const stop = (keep: boolean) => {
    const r = recorder.current;
    if (!r) return;
    (r as MediaRecorder & { keep?: boolean }).keep = keep;
    r.stop();
    recorder.current = null;
    setRec(null);
  };

  const onMicDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    void start(e.clientX, e.clientY);
  };
  const onMicMove = (e: PointerEvent) => {
    if (!rec) return;
    const far = Math.hypot(e.clientX - rec.x, e.clientY - rec.y) > CANCEL_DISTANCE;
    if (far !== rec.cancel) setRec({ ...rec, cancel: far });
  };
  const onMicUp = () => rec && stop(!rec.cancel);
  const onMicKey = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (recorder.current) stop(true);
    else void start(0, 0);
  };

  const pickPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    const out: OutFile[] = [];
    for (const f of [...files].slice(0, 6)) {
      try {
        out.push({ body: await shrinkImage(f), mime: 'image/jpeg', ext: 'jpg' });
      } catch {
        toast.error(t('common.error'));
      }
    }
    if (out.length) onSend(text.trim(), out);
    setText('');
    if (photo.current) photo.current.value = '';
  };

  const secs = Math.floor(elapsed / 1000);
  return (
    <div className="px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2">
      {rec ? (
        <div
          className={cn(
            'mx-auto mb-2 flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm shadow-card',
            rec.cancel ? 'bg-gap/10 text-gap' : 'bg-panel text-ink',
          )}
          role="status"
        >
          {rec.cancel ? (
            <Trash2 className="size-4" aria-hidden />
          ) : (
            <span className="size-2.5 animate-pulse rounded-full bg-gap" aria-hidden />
          )}
          <span className="tabular-nums">
            {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
          </span>
          <span className="truncate text-muted">{t('chat.recording')}</span>
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <input
          ref={photo}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => void pickPhotos(e.target.files)}
        />
        <div className="flex min-h-13 flex-1 items-end rounded-[26px] bg-panel/90 p-1 shadow-card ring-1 ring-line/50 backdrop-blur">
          <button
            type="button"
            aria-label={t('chat.photo')}
            disabled={disabled}
            onClick={() => photo.current?.click()}
            className="grid size-11 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-soft hover:text-ink disabled:opacity-45"
          >
            <Plus className="size-5" />
          </button>
          <span className="my-3 w-px self-stretch bg-line" aria-hidden />
          <textarea
            ref={box}
            rows={1}
            dir="auto"
            value={text}
            disabled={disabled}
            placeholder={t('chat.placeholder')}
            aria-label={t('chat.placeholder')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends on a keyboard; on phones the return key adds a line
              if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
                e.preventDefault();
                send();
              }
            }}
            className="field-sizing-content max-h-36 min-h-11 flex-1 resize-none bg-transparent px-3 py-2.5 leading-6 outline-none placeholder:text-muted"
          />
        </div>
        {text.trim() ? (
          <Button
            size="icon"
            aria-label={t('chat.send')}
            disabled={disabled}
            onClick={send}
            className="size-13 shadow-pop"
          >
            <SendHorizontal className="rtl:-scale-x-100" />
          </Button>
        ) : (
          <Button
            size="icon"
            aria-label={t('chat.hold_to_talk')}
            title={t('chat.hold_to_talk')}
            disabled={disabled}
            className={cn('size-13 touch-none shadow-pop', rec && 'scale-110 bg-gap')}
            onPointerDown={onMicDown}
            onPointerMove={onMicMove}
            onPointerUp={onMicUp}
            onPointerCancel={() => stop(false)}
            onKeyDown={onMicKey}
            onContextMenu={(e) => e.preventDefault()}
          >
            <Mic />
          </Button>
        )}
      </div>
    </div>
  );
}
